import {
	alpha,
	Avatar,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Activity } from '../../../@types';
import { constructActivityItems } from '../../../utils/activities';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { radius } from '../../../utils/designSystem';

export interface DialogAuditTimelineProps {
	activities: Activity[];
	isLoading?: boolean;
	isOpen: boolean;
	onClose: () => void;
	title?: string;
}

export const DialogAuditTimeline = ({
	activities,
	isLoading = false,
	isOpen,
	onClose,
	title = 'Audit Log',
}: DialogAuditTimelineProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const items = constructActivityItems({ activities, t });

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			PaperProps={{ sx: { borderRadius: radius.lg, maxHeight: '80vh' } }}>
			{/* Header */}
			<Box
				sx={{
					px: 3,
					py: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 36, height: 36 }}>
						<HistoryIcon sx={{ color: 'primary.main', fontSize: 20 }} />
					</Avatar>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
				</Stack>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</Box>

			<DialogContent sx={{ p: 0 }}>
				{isLoading ? (
					<Box sx={{ py: 4, textAlign: 'center' }}>
						<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>
						{t('loadingAuditLog')}
						</Typography>
					</Box>
				) : items.length === 0 ? (
					<Box sx={{ py: 4, textAlign: 'center' }}>
						<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>
						{t('noAuditHistory')}
						</Typography>
					</Box>
				) : (
					<Box>
						{items.map((item, idx) => (
							<Stack
								key={item.activity?.id ?? idx}
								direction="row"
								alignItems="center"
								spacing={2}
								sx={{
									px: 2.5,
									py: 1.5,
									bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
								}}>
								{/* Icon */}
								<Box sx={{ flexShrink: 0 }}>
									<IconCircle type={item.type || 'other'} size="sm" />
								</Box>

								{/* Title + description + note */}
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ fontWeight: 600, fontSize: '1.3rem' }} noWrap>
										{item.title}
									</Typography>
									<Box sx={{ color: 'text.secondary', '& > p': { fontSize: '1.2rem', m: 0 } }}>
										{item.content}
									</Box>
								</Box>

								{/* Actor name + date */}
								<Box sx={{ flexShrink: 0 }}>
									<UserAvatarModified activity={item.activity} />
								</Box>
							</Stack>
						))}
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
				<Button
					variant="outlined"
					onClick={onClose}
					sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
					{t('close')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
